import { getSupabaseAuthToken } from '@/lib/supabaseClient'

interface SignedUrlParams {
  dropId: string
  contributorId: string
  filename: string
  contentType: string
  contributorToken: string
}

interface SignedUrlResponse {
  uploadUrl: string
  key: string
}

export interface IssuedContributorToken {
  token: string
  contributorId: string
  dropId: string
  expiresAt: number
}

export async function issueContributorToken(
  workerUrl: string,
  rawToken: string
): Promise<IssuedContributorToken> {
  const res = await fetch(`${workerUrl}/issue-contributor-token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rawToken }),
  })

  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText)
    throw new Error(`Failed to issue contributor token: ${text}`)
  }

  return res.json()
}

export async function getSignedUploadUrl(
  workerUrl: string,
  params: SignedUrlParams
): Promise<SignedUrlResponse> {
  const res = await fetch(`${workerUrl}/generate-upload-url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      dropId: params.dropId,
      contributorId: params.contributorId,
      filename: params.filename,
      contentType: params.contentType,
      contributorToken: params.contributorToken,
    }),
  })

  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText)
    throw new Error(`Failed to get upload URL: ${text}`)
  }

  return res.json()
}

export async function uploadFileToR2(
  uploadUrl: string,
  file: File,
  contentType: string
): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: file,
  })

  if (!res.ok) {
    throw new Error(`R2 upload failed: ${res.status} ${res.statusText}`)
  }
}

export interface ModerateDeletePhotoResponse {
  deleted: boolean
  deletedKeys: string[]
}

// Moderator-only photo deletion. Removes the `photos` row and best-
// effort deletes the R2 objects for original/processed/thumbnail variants.
// Authorization is enforced server-side: caller must be owner or admin of
// the organization that owns the photo's drop.
export async function moderateDeletePhoto(
  workerUrl: string,
  photoId: string
): Promise<ModerateDeletePhotoResponse> {
  const token = await getSupabaseAuthToken()
  const res = await fetch(`${workerUrl}/moderate-delete-photo`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ photoId }),
  })
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText)
    throw new Error(`Failed to delete photo: ${text}`)
  }
  return res.json()
}

export function triggerProcessing(
  workerUrl: string,
  params: {
    key: string
    tier: 'free' | 'pro' | 'business'
    photoId: string
    organizationId: string
    sessionId?: string
  }
): void {
  // Fire-and-forget — processing happens async, contributor doesn't need to wait
  fetch(`${workerUrl}/process-upload`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  }).catch((err) => {
    console.error('Failed to trigger image processing:', err)
  })
}
